import { Divider, message, Tabs, TabsProps } from "antd";
import {
  ServiceScopeData,
  usePartnerOnboarding,
} from "../../contexts/PartnerOnboardingContext";
import MarketIndicator from "../components/BrandsPartnerComponents/MarketIndicator";
import NavigationButton from "../components/BrandsPartnerComponents/navigation_button";
import { PlusOutlined } from "@ant-design/icons";
import MultiSelectCheckboxGroup from "../components/BrandsPartnerComponents/multiselect_checkbox";
import { useEffect, useRef, useState } from "react";
import AddMarketModal from "../components/BrandsPartnerComponents/Modals/AddMarketsModal";
import DeleteConfirmationModal from "../components/BrandsPartnerComponents/Modals/DeleteConfirmationModal";
import { GetRegionsList } from "../../API/endpoints/Regions";
import { PartnerMarketType, PartnerType } from "./dtos/partner.dtos";
import SimpleSingleSelectButtonGroup from "../components/BrandsPartnerComponents/SingleSelectRadioButton";
import MultiSelectWithAddItem from "../components/BrandsPartnerComponents/MultiselectComponentWithAddItem";
import { set } from "react-hook-form";

interface Props {
  previousStep: () => void;
  nextStep: () => void;
  data: PartnerType;
  setData: React.Dispatch<React.SetStateAction<PartnerType>>;
}
const ServiceAndScopePage: React.FC<Props> = ({
  previousStep,
  nextStep,
  data,
  setData,
}) => {
  const { selectedMarkets, setSelectedMarkets, localMarkets } =
    usePartnerOnboarding();
  const [serviceScope, setServiceScope] = useState<Market["serviceScope"]>({
    healthSupplements: [],
    beautyAndSkin: [],
    foodAndBeverages: [],
    petNutrition: [],
    keyChannelCoverage: [],
    keyCapabilities: [],
    warehousing: [],
    acceptedBusinessModels: [],
    tradingTerms: [],
    minimumExclusivityTenure: [],
    preferenceonMarketingInvestment: [],
    paymentTerms: [],
  });
  const [activeTab, setActiveTab] = useState("focus");
  const [checkedHealthSuppliments, setCheckedHealthSuppliments] =
    useState(true);
  const [checkedBeauty, setCheckedBeauty] = useState(true);
  const [checkedFood, setCheckedFood] = useState(true);
  const [otherInputValueFAB, setOtherInputValueFAB] = useState("");
  const [checkedPetNutrition, setCheckedPetNutrition] = useState(true);
  const { customOptionsMap, setCustomOptionsMap } = usePartnerOnboarding();

  const focusRef = useRef<HTMLDivElement>(null);
  const channelsRef = useRef<HTMLDivElement>(null);
  const engagementRef = useRef<HTMLDivElement>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const [activeMarket, setActiveMarket] = useState<string>(
    selectedMarkets[0]?.name
  );

  interface OptionProps {
    id: string;
    name: string;
  }
  // Define the Market type if not already defined or imported
  interface Market {
    name: string;
    serviceScope: {
      healthSupplements: string[];
      beautyAndSkin: string[];
      foodAndBeverages: string[];
      petNutrition: string[];
      keyChannelCoverage: string[];
      keyCapabilities: string[];
      warehousing: string[];
      acceptedBusinessModels: string[];
      tradingTerms: string[];
      minimumExclusivityTenure: string[];
      preferenceonMarketingInvestment: string[];
      paymentTerms: string[];
      [key: string]: any; // Add additional fields as needed
    };
  }

  const updateMarketData = (
    marketName: string,
    newScope: Partial<Market["serviceScope"]>
  ) => {
    setSelectedMarkets((prev) =>
      prev.map((market) =>
        market.name === marketName
          ? {
              ...market,
              serviceScope: {
                ...market.serviceScope,
                ...newScope,
              },
            }
          : market
      )
    );
  };

  function useAutoTabSwitch(
    refs: Record<string, React.RefObject<HTMLElement>>,
    setActiveTab: (key: string) => void
  ) {
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (isScrollingManually.current) return;
          // Sort entries by vertical position on screen
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort(
              (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
            );

          if (visible.length > 0) {
            const topMost = visible[0];
            const key = Object.entries(refs).find(
              ([, ref]) => ref.current === topMost.target
            )?.[0];

            if (key) {
              setActiveTab(key);
            }
          }
        },
        {
          root: document.querySelector("#scrollable-content"), // IMPORTANT: restrict to scroll container
          rootMargin: "-80px 0px 0px 0px", // adjust for sticky header height
          threshold: 0.25,
        }
      );

      Object.values(refs).forEach((ref) => {
        if (ref.current) observer.observe(ref.current);
      });

      return () => observer.disconnect();
    }, [refs, setActiveTab]);
  }

  useEffect(() => {
    const marketData = selectedMarkets.find((m) => m.name === activeMarket);
    if (marketData) {
      setServiceScope(marketData.serviceScope);
    }
  }, [activeMarket, selectedMarkets]);

  const handleTabChange = (key: string) => {
    setActiveTab(key); // visually update tab highlight immediately

    const sectionMap: Record<string, React.RefObject<HTMLDivElement>> = {
      focus: focusRef,
      channels: channelsRef,
      engagement: engagementRef,
    };

    const targetRef = sectionMap[key];
    const scrollContainer = document.getElementById("scrollable-content");
    const vhToPixels = (vh: number) => {
      return (window.innerHeight * vh) / 100;
    };
    const scrollOffset = vhToPixels(32); // converts 10vh to pixels

    if (targetRef?.current && scrollContainer) {
      isScrollingManually.current = true;
      scrollContainer.scrollTo({
        top: targetRef.current.offsetTop - scrollOffset,
        behavior: "smooth",
      });
      // Re-enable observer logic after scrolling finishes
      setTimeout(() => {
        isScrollingManually.current = false;
      }, 1000); // enough time for scroll animation to finish
    }
  };

  const sectionRefs = {
    focus: focusRef,
    channels: channelsRef,
    engagement: engagementRef,
  };

  const isScrollingManually = useRef(false);

  useAutoTabSwitch(sectionRefs, setActiveTab);

  const foodAndBeverages = [
    "None",
    "Bars",
    "Snacking",
    "Ready To Mix Beverages",
    "Replacement Meals",
    "RTD Beverages",
    // 'Other',
  ];
  const tabItems: TabsProps["items"] = [
    { key: "focus", label: "Focus Category" },
    { key: "channels", label: "Channel & Capabilities" },
    { key: "engagement", label: "Engagement Terms" },
  ];
  const beautyAndSkin = [
    "Skincare",
    "Hair Care",
    "Body Care",
    "Wellness & Inner Beauty",
    "Dermatology & Treatments",
  ];
  const healthSupplements = [
    "Vitamins & Minerals",
    "Sports & Fitness Supplements",
    "Men’s Health",
    "Digestive Health",
    "Herbal & Natural Supplements",
    "Weight Management",
    "Mental Health & Sleep Support",
    "Heart & Cardiovascular Health",
    "Immune Support & Wellness",
    "Women’s Health",
    // "Other"
  ];
  const petNutrition = [
    "Dental",
    "Food",
    "Supplements",
    "Snacking",
    // "Other"
  ];
  const keyChannelCoverage = [
    "None",
    "Offline Retail",
    "Online",
    "Food Service/Out of Home",
  ];
  const keyCapabilities = [
    "Regulatory Compliance",
    "Importation & Clearance",
    "Warehousing",
    "Distribution",
    "Sales",
    "Marketing",
  ];
  const warehousing = ["Cold & Frozen", "Ambient"];
  const businessmodels = ["Pre-Purchase", "Consignment", "Both"];
  const tradingTerms = ["Exclusive", "Non-Exclusive", "Both"];

  const marketing_investment = [
    "Partner Responsibility",
    "Brand Responsibility",
    "Co - Responsibility",
  ];
  const minimumExclusivityTenure = ["3 yrs or more", "2 yrs", "1 yr"];
  const paymentTerms = ["Escrow", "COD", "Bank LC", "30-60 Days Credit"];
  const [allMarkets, setAllMarkets] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const response = await GetRegionsList();
        setAllMarkets(response.data);
      } catch (error) {}
    })();
  }, []);
  const [isAddMarketModalOpen, setAddMarketIsModalOpen] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<OptionProps[] | null>(
    null
  );

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [marketToDelete, setMarketToDelete] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<{
    [key: string]: string;
  }>({});

  // Handler when clicking the trash icon
  const handleDeleteClick = (marketName: string) => {
    setMarketToDelete(marketName);
    setDeleteModalVisible(true);
  };

  // Confirm deletion
  const confirmDelete = () => {
    if (marketToDelete) {
      setSelectedMarkets((prev) =>
        prev.filter((m) => m.name !== marketToDelete)
      );
      setData((current) => {
        let currentMarkets = current.markets || {};

        let updatedMarkets = { ...currentMarkets };
        if (updatedMarkets[marketToDelete]) {
          delete updatedMarkets[marketToDelete];
        }
        return {
          ...current,
          markets: updatedMarkets,
        };
      });
      setDeleteModalVisible(false);
      setMarketToDelete(null);
    }
  };
  const updateMarketFocusCategory = (
    activeMarket: string,
    category: string,
    subCategories: string[],
    setData: React.Dispatch<React.SetStateAction<PartnerType>>
  ) => {
    if (!activeMarket) return;
    setData((current) => {
      const newMarkets = new Map(Object.entries(current.markets));
      const currentMarkets = current.markets;
      const existingMarket = newMarkets.get(activeMarket) || {
        focus_categories: {},
        region: 0,
        channel_coverage: [],
        service_capabilities: [],
        warehousing: [],
        engagement_model: [],
        trading_terms: "",
        minimum_tenure: "",
        trade_marketing_investments: "",
        payment_terms: [],
      };

      const updatedFocusCategories = {
        ...existingMarket.focus_categories,
        [category]: subCategories,
      };

      const matchedMarket = selectedMarkets.find(
        (m) => m.name === activeMarket
      );
      const regionId = Number(matchedMarket?.id ?? 0);

      newMarkets.set(activeMarket, {
        ...existingMarket,
        focus_categories: updatedFocusCategories,
        region: regionId,
      });

      return {
        ...current,
        markets: {
          ...currentMarkets,
          [activeMarket]: {
            ...existingMarket,
            region: regionId,
            focus_categories: updatedFocusCategories,
          },
        },
      };
    });
  };

  const getSelectedMarketCategoryLength = (activeMarket: string) => {
    const marketData = selectedMarkets.find(
      (market) => market.name === activeMarket
    );

    if (marketData) {
      const { serviceScope } = marketData;

      // Check the length of each category of interest
      const healthSupplementsLength = serviceScope.healthSupplements.length;
      const beautyAndSkinLength = serviceScope.beautyAndSkin.length;
      const foodAndBeveragesLength = serviceScope.foodAndBeverages.length;
      const petNutritionLength = serviceScope.petNutrition.length;
      const keyChannelCoverageLength = serviceScope.keyChannelCoverage.length;
      const keyCapabilitiesLength = serviceScope.keyCapabilities.length;
      const wareHousingLength = serviceScope.warehousing.length;
      const businessModelLength = serviceScope.acceptedBusinessModels.length;
      const tradingLength = serviceScope.tradingTerms.length;
      const minimumExclusivityTenureLength =
        serviceScope.minimumExclusivityTenure.length;
      const marketingInvLength =
        serviceScope.preferenceonMarketingInvestment.length;
      const paymentLength = serviceScope.paymentTerms.length;

      // You can also return this as an object if needed
      return {
        healthSupplements: healthSupplementsLength,
        beautyAndSkin: beautyAndSkinLength,
        foodAndBeverages: foodAndBeveragesLength,
        petNutrition: petNutritionLength,
        keyChannelCoverage: keyChannelCoverageLength,
        keyCapabilities: keyCapabilitiesLength,
        wareHousing: wareHousingLength,
        acceptedBusiness: businessModelLength,
        tradingTerms: tradingLength,
        minimumExclusivityTenure: minimumExclusivityTenureLength,
        marketingInv: marketingInvLength,
        paymentTerms: paymentLength,
      };
    }

    return null; // In case no market data found
  };

  const updateMarketServiceCapabilities = (
    activeMarket: string,
    category: keyof PartnerMarketType,
    subCategories: string[] | string,
    setData: React.Dispatch<React.SetStateAction<PartnerType>>
  ) => {
    setData((current) => {
      const currentMarkets = current.markets || {};

      const existingMarket = currentMarkets[activeMarket] || {
        focus_categories: new Map<string, string[]>(), // or convert this to a plain object too
        region: 0,
        channel_coverage: [],
        service_capabilities: [],
        warehousing: [],
        engagement_model: [],
        trading_terms: "",
        minimum_tenure: "",
        trade_marketing_investments: "",
        payment_terms: [],
      };

      const matchedMarket = selectedMarkets.find(
        (m) => m.name === activeMarket
      );
      const regionId = matchedMarket?.id ?? 0;

      const updatedMarket = {
        ...existingMarket,
        [category]: subCategories,

        region: Number(regionId),
      };

      return {
        ...current,
        markets: {
          ...currentMarkets,
          [activeMarket]: updatedMarket,
        },
      };
    });
  };

  const areAllCategoriesValid = () => {
    const isNonEmptyString = (value: any): boolean => {
      return typeof value === "string" && value.trim() !== "";
    };

    const isNonEmptyArray = (value: any): boolean => {
      return Array.isArray(value) && value.length > 0;
    };

    const isNonEmptyObject = (value: any): boolean => {
      return (
        typeof value === "object" &&
        value !== null &&
        Object.keys(value).length > 0
      );
    };

    for (const marketCode in data.markets) {
      const market = data.markets[marketCode];
      if (
        !isNonEmptyArray(market.channel_coverage) ||
        !isNonEmptyArray(market.service_capabilities) ||
        !isNonEmptyObject(market.focus_categories) ||
        !isNonEmptyArray(market.engagement_model) ||
        !isNonEmptyString(market.trading_terms) ||
        !isNonEmptyString(market.minimum_tenure) ||
        !isNonEmptyString(market.trade_marketing_investments) ||
        !isNonEmptyArray(market.payment_terms)
      ) {
        return false;
      }

      if (
        market.service_capabilities.includes("Warehousing") &&
        !isNonEmptyArray(market.warehousing)
      ) {
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    const categoryLengths = getSelectedMarketCategoryLength(activeMarket);

    if (categoryLengths) {
      const {
        keyChannelCoverage,
        keyCapabilities,
        wareHousing,
        acceptedBusiness,
        tradingTerms,
        marketingInv,
        paymentTerms,
        minimumExclusivityTenure,
      } = categoryLengths;

      // Initialize an empty array for error messages
      let errorMessage: { [key: string]: string } = {};
      // Check for Focus Categories
      if (
        typeof data.markets[activeMarket]?.focus_categories === "object" &&
        data.markets[activeMarket]?.focus_categories !== null &&
        Object.keys(data.markets[activeMarket]?.focus_categories).length === 0
      ) {
        errorMessage.focusCategory =
          "Please select at least one item in Focus category. ";
      }

      // Check for Channel Capabilities
      if (keyChannelCoverage === 0) {
        errorMessage.keychannel =
          "Please select at least one item in Key channel Coverage. ";
      }
      if (keyCapabilities === 0) {
        errorMessage.keyCapabilities =
          "Please select at least one item in Key capabilities. ";
      }
      if (acceptedBusiness === 0) {
        errorMessage.businessModel =
          "Please select at least one item in Accepted business model. ";
      }
      if (tradingTerms === 0) {
        errorMessage.tradingTerms =
          "Please select at least one item in Trading terms. ";
      }
      if (marketingInv === 0) {
        errorMessage.marketingInv =
          "Please select at least one item in Marketing investment. ";
      }
      if (paymentTerms === 0) {
        errorMessage.paymentTerms =
          "Please select at least one item in Payment terms. ";
      }
      if (minimumExclusivityTenure === 0) {
        errorMessage.minimumExclusivityTenure =
          "Please select at least one item in Minimum exlusivity tenure. ";
      }
      if (
        data.markets[activeMarket]?.service_capabilities.includes(
          "Warehousing"
        ) &&
        wareHousing === 0
      ) {
        errorMessage.Warehousing =
          "Please select at least one item in Warehousing. ";
      }

      const objKeys = Object.keys(errorMessage);
      // Set validation error or proceed
      if (objKeys.length > 0) {
        messageApi.error(errorMessage[objKeys[0]]);
        setValidationError(errorMessage);
      } else {
        setValidationError({}); // Clear any previous error
        if (!areAllCategoriesValid()) {
          messageApi.error(
            "Please complete fill service and scope for remaining markets"
          );
        } else nextStep(); // Proceed to next step if valid
      }
    }
  };

  return (
    <div className="flex flex-col px-[100px]">
      {contextHolder}
      {/* Top section */}
      <div className="z-20 ">
        <div className="flex items-between justify-between overflow-hidden">
          {" "}
          <div className="common-title pb-[12px]">Service & Scope</div>
          <NavigationButton
            type="text"
            disable={
              allMarkets.length === selectedMarkets.length ? true : false
            }
            icon={<PlusOutlined />}
            onClick={() => setAddMarketIsModalOpen(true)}
            label="Add New"
          />
        </div>{" "}
      </div>
      <div className="overflow-x-auto whitespace-nowrap  py-2 px-1 custom-scrollbar ">
        <div className="inline-flex gap-4 items-end bg-white">
          {selectedMarkets.map((market: any) => {
            // const selectedOptions = Object.values(
            //   market.serviceScope || {}
            // ).filter((arr) => Array.isArray(arr) && arr.length > 0).length;
            const focusCategories = [
              "healthSupplements",
              "beautyAndSkin",
              "foodAndBeverages",
              "petNutrition",
            ];

            const isFocusSelected = focusCategories.some(
              (cat) =>
                Array.isArray(market.serviceScope?.[cat]) &&
                market.serviceScope[cat].length > 0
            );
            console.log("market.serviceScope", market.serviceScope);
            const nonFocusSelectedCount = Object.entries(
              market.serviceScope || {}
            ).filter(
              ([key, arr]) =>
                !focusCategories.includes(key) &&
                Array.isArray(arr) &&
                arr.length > 0 &&
                key !== "warehousing"
            ).length;

            const selectedOptions =
              (isFocusSelected ? 1 : 0) + nonFocusSelectedCount;

            return (
              <MarketIndicator
                key={market.name}
                marketName={market.name}
                onclick={() => setActiveMarket(market.name)}
                activeMarket={activeMarket}
                selectedOptions={selectedOptions}
                totalOptions={8}
                onDelete={() => handleDeleteClick(market.name)}
              />
            );
          })}
        </div>
      </div>

      <Divider className=" !border-[#d0d5dd] !pY-8"></Divider>

      {/* Tabbar and content */}
      <div className=" max-w-[1000px] min-h-screen">
        <div>
          <div className="flex inline-flex gap-[40px]">
            <div className="w-[260px] px-4 py-6 ">
              <div className="z-10">
                <Tabs
                  tabPosition="right"
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  tabBarGutter={4}
                  tabBarStyle={{
                    padding: 0,
                    marginRight: 0,
                  }}
                  className="custom-tab-style"
                  items={tabItems.map((item) => ({
                    ...item,
                    label: (
                      <div
                        className={`w-[200px] px-[14px] text-left py-[10px] rounded-md leading-[19px] font-['Inter'] text-[13px] ${
                          item.key === activeTab
                            ? "text-[#1849a9] font-medium bg-[#eff8ff]"
                            : "text-[#475467] font-normal"
                        }`}
                      >
                        {item.label}
                      </div>
                    ),
                  }))}
                />
              </div>
              <div
                className={` mt-[20vh] inline-flex flex justify-end  gap-x-[8px] w-full md:w-auto`}
              >
                {
                  <NavigationButton
                    onClick={previousStep}
                    label="Previous"
                    type="text"
                  />
                }
                <NavigationButton
                  onClick={handleNext}
                  label={"Next"}
                  type="primary"
                />
              </div>
            </div>
            <div
              className="flex-1 overflow-y-auto px-8 py-6 space-y-12 scroll-smooth items-end justify-end scrollbar-hide"
              id="scrollable-content"
              style={{ maxHeight: "82vh" }}
            >
              <div ref={focusRef}>
                {
                  <div className="flex flex-col gap-y-8">
                    <div className="flex flex-col">
                      <div className="self-stretch px-2.5 py-1 bg-[#f2f4f7] rounded inline-flex justify-center items-center gap-2.5 overflow-hidden">
                        <div className="flex-1 justify-start text-[#1d2939] text-sm font-medium font-['Inter'] leading-tight">
                          Focus Category
                        </div>
                      </div>
                      {validationError.focusCategory && (
                        <div className="text-red-500 text-sm !my-0">
                          {validationError.focusCategory}
                        </div>
                      )}
                    </div>

                    <MultiSelectWithAddItem
                      customOptionsMap={customOptionsMap}
                      setCustomOptionsMap={setCustomOptionsMap}
                      activeMarket={activeMarket}
                      label="Health Supplements"
                      contextKey="healthSupplements"
                      options={healthSupplements}
                      showAddCustom
                      selected={
                        data.markets[activeMarket]?.focus_categories?.[
                          "Health Supplements"
                        ] || []
                      }
                      setSelected={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          healthSupplements: val,
                        }));
                        updateMarketData(activeMarket, {
                          healthSupplements: val,
                        });

                        updateMarketFocusCategory(
                          activeMarket,
                          "Health Supplements",
                          val,
                          setData
                        );
                      }}
                      checked={
                        typeof data.markets[activeMarket]?.focus_categories ===
                          "object" &&
                        data.markets[activeMarket]?.focus_categories !== null &&
                        Object.keys(
                          data.markets[activeMarket]?.focus_categories
                        ).includes("Health Supplements")
                      }
                      setChecked={() =>
                        setData((current) => {
                          let currentCategories =
                            current.markets[activeMarket]?.focus_categories ||
                            {};
                          let updatedCategories = { ...currentCategories };

                          if (updatedCategories["Health Supplements"]) {
                            delete updatedCategories["Health Supplements"];
                          } else {
                            updatedCategories["Health Supplements"] = [];
                          }

                          return {
                            ...current,
                            markets: {
                              ...current.markets,
                              [activeMarket]: {
                                ...current.markets[activeMarket],
                                focus_categories: updatedCategories,
                              },
                            },
                          };
                        })
                      }
                      showCheckBox={true}
                    />
                    {/* Beauty & Skin */}
                    <MultiSelectWithAddItem
                      customOptionsMap={customOptionsMap}
                      setCustomOptionsMap={setCustomOptionsMap}
                      activeMarket={activeMarket}
                      showAddCustom
                      label="Beauty & Skin"
                      contextKey="beautyAndSkin"
                      options={beautyAndSkin}
                      selected={
                        data.markets[activeMarket]?.focus_categories?.[
                          "Beauty & Skin"
                        ] || []
                      }
                      setSelected={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          beautyAndSkin: val,
                        }));
                        updateMarketData(activeMarket, { beautyAndSkin: val });
                        updateMarketFocusCategory(
                          activeMarket,
                          "Beauty & Skin",
                          val,
                          setData
                        );
                      }}
                      checked={
                        typeof data.markets[activeMarket]?.focus_categories ===
                          "object" &&
                        data.markets[activeMarket]?.focus_categories !== null &&
                        Object.keys(
                          data.markets[activeMarket]?.focus_categories
                        ).includes("Beauty & Skin")
                      }
                      setChecked={() =>
                        setData((current) => {
                          let currentCategories =
                            current.markets[activeMarket]?.focus_categories ||
                            {};
                          let updatedCategories = { ...currentCategories };

                          if (updatedCategories["Beauty & Skin"]) {
                            delete updatedCategories["Beauty & Skin"];
                          } else {
                            updatedCategories["Beauty & Skin"] = [];
                          }

                          return {
                            ...current,
                            markets: {
                              ...current.markets,
                              [activeMarket]: {
                                ...current.markets[activeMarket],
                                focus_categories: updatedCategories,
                              },
                            },
                          };
                        })
                      }
                      showCheckBox={true}
                    />
                    {/* Functional Food & Beverages */}
                    <MultiSelectWithAddItem
                      customOptionsMap={customOptionsMap}
                      setCustomOptionsMap={setCustomOptionsMap}
                      activeMarket={activeMarket}
                      showAddCustom
                      label="Functional Food & Beverages"
                      contextKey="functionalFoodAndBeverages"
                      options={foodAndBeverages}
                      selected={
                        data.markets[activeMarket]?.focus_categories?.[
                          "Functional Food & Beverages"
                        ] || []
                      }
                      setSelected={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          foodAndBeverages: val,
                        }));
                        updateMarketData(activeMarket, {
                          foodAndBeverages: val,
                        });
                        const updated = val.map((m) =>
                          m === "Other" ? otherInputValueFAB : m
                        );
                        updateMarketFocusCategory(
                          activeMarket,
                          "Functional Food & Beverages",
                          updated,
                          setData
                        );
                      }}
                      checked={
                        typeof data.markets[activeMarket]?.focus_categories ===
                          "object" &&
                        data.markets[activeMarket]?.focus_categories !== null &&
                        Object.keys(
                          data.markets[activeMarket]?.focus_categories
                        ).includes("Functional Food & Beverages")
                      }
                      setChecked={() =>
                        setData((current) => {
                          let currentCategories =
                            current.markets[activeMarket]?.focus_categories ||
                            {};
                          let updatedCategories = { ...currentCategories };

                          if (
                            updatedCategories["Functional Food & Beverages"]
                          ) {
                            delete updatedCategories[
                              "Functional Food & Beverages"
                            ];
                          } else {
                            updatedCategories["Functional Food & Beverages"] =
                              [];
                          }

                          return {
                            ...current,
                            markets: {
                              ...current.markets,
                              [activeMarket]: {
                                ...current.markets[activeMarket],
                                focus_categories: updatedCategories,
                              },
                            },
                          };
                        })
                      }
                      otherInputValue={otherInputValueFAB}
                      setOtherInputValue={setOtherInputValueFAB}
                    />
                    {/* Pet Nutrition */}
                    <MultiSelectWithAddItem
                      customOptionsMap={customOptionsMap}
                      setCustomOptionsMap={setCustomOptionsMap}
                      activeMarket={activeMarket}
                      showAddCustom
                      label="Pet Nutrition"
                      contextKey="petNutrition"
                      options={petNutrition}
                      selected={
                        data.markets[activeMarket]?.focus_categories?.[
                          "Pet Nutrition"
                        ] || []
                      }
                      setSelected={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          petNutrition: val,
                        }));
                        updateMarketData(activeMarket, { petNutrition: val });
                        updateMarketFocusCategory(
                          activeMarket,
                          "Pet Nutrition",
                          val,
                          setData
                        );
                      }}
                      checked={
                        typeof data.markets[activeMarket]?.focus_categories ===
                          "object" &&
                        data.markets[activeMarket]?.focus_categories !== null &&
                        Object.keys(
                          data.markets[activeMarket]?.focus_categories
                        ).includes("Pet Nutrition")
                      }
                      setChecked={() =>
                        setData((current) => {
                          let currentCategories =
                            current.markets[activeMarket]?.focus_categories ||
                            {};
                          let updatedCategories = { ...currentCategories };

                          if (updatedCategories["Pet Nutrition"]) {
                            delete updatedCategories["Pet Nutrition"];
                          } else {
                            updatedCategories["Pet Nutrition"] = [];
                          }

                          return {
                            ...current,
                            markets: {
                              ...current.markets,
                              [activeMarket]: {
                                ...current.markets[activeMarket],
                                focus_categories: updatedCategories,
                              },
                            },
                          };
                        })
                      }
                    />
                  </div>
                }
              </div>
              <div ref={channelsRef}>
                {/* Channel & Capabilities section */}
                {
                  <div className="flex flex-col gap-y-8">
                    <div className="self-stretch px-2.5 py-1 bg-[#f2f4f7] rounded inline-flex justify-center items-center gap-2.5 overflow-hidden">
                      <div className="flex-1 justify-start text-[#1d2939] text-sm font-medium font-['Inter'] leading-tight">
                        Channel & Capabilities
                      </div>
                    </div>

                    <MultiSelectWithAddItem
                      customOptionsMap={customOptionsMap}
                      setCustomOptionsMap={setCustomOptionsMap}
                      error={validationError.keychannel ?? ""}
                      activeMarket={activeMarket}
                      label="Key Channel Coverage"
                      contextKey="keyChannelCoverage"
                      options={keyChannelCoverage}
                      selected={
                        data.markets[activeMarket]?.channel_coverage ?? []
                      }
                      setSelected={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          keyChannelCoverage: val,
                        }));
                        updateMarketData(activeMarket, {
                          keyChannelCoverage: val,
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "channel_coverage",
                          val,
                          setData
                        );
                      }}
                      checked={true}
                      setChecked={() => {}}
                      showCheckBox={false}
                    />
                    <MultiSelectCheckboxGroup
                      label="Key Capabilities"
                      error={validationError.keyCapabilities ?? ""}
                      options={keyCapabilities}
                      selected={
                        data.markets[activeMarket]?.service_capabilities || []
                      }
                      setSelected={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          keyCapabilities: val,
                        }));
                        updateMarketData(activeMarket, {
                          keyCapabilities: val,
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "service_capabilities",
                          val,
                          setData
                        );
                      }}
                      checked={true}
                      showCheckBox={false}
                      setChecked={() => {}}
                    />

                    {data.markets[activeMarket]?.service_capabilities.includes(
                      "Warehousing"
                    ) && (
                      <MultiSelectCheckboxGroup
                        label="Warehousing"
                        error={validationError.Warehousing ?? ""}
                        options={warehousing}
                        selected={data.markets[activeMarket]?.warehousing || []}
                        setSelected={(val) => {
                          setServiceScope((prev) => ({
                            ...prev,
                            warehousing: val,
                          }));
                          updateMarketData(activeMarket, { warehousing: val });
                          updateMarketServiceCapabilities(
                            activeMarket,
                            "warehousing",
                            val,
                            setData
                          );
                        }}
                        checked={true}
                        showCheckBox={false}
                        setChecked={() => {}}
                      />
                    )}
                  </div>
                }
              </div>
              <div ref={engagementRef}>
                {/* Engagement Terms section */}

                {
                  <div className="flex flex-col gap-y-8 pb-[30vh]">
                    <div className="self-stretch px-2.5 py-1 bg-[#f2f4f7] rounded inline-flex justify-center items-center gap-2.5 overflow-hidden">
                      <div className="flex-1 justify-start text-[#1d2939] text-sm font-medium font-['Inter'] leading-tight">
                        Engagement Terms
                      </div>
                    </div>
                    {/* Accepted Business Models  */}
                    <SimpleSingleSelectButtonGroup
                      error={validationError.businessModel ?? ""}
                      label="Accepted Business Models"
                      options={businessmodels}
                      selectedValue={
                        String(data.markets[activeMarket]?.engagement_model) ||
                        ""
                      }
                      showRadioButton={true}
                      setSelectedValue={(val) => {
                        setServiceScope((prev) => ({
                          ...prev,
                          acceptedBusinessModels: [val],
                        }));
                        updateMarketData(activeMarket, {
                          acceptedBusinessModels: [val],
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "engagement_model",
                          [val],
                          setData
                        );
                      }}
                    />{" "}
                    {/* Trading Terms*/}
                    <SimpleSingleSelectButtonGroup
                      error={validationError.tradingTerms ?? ""}
                      label="Trading Terms"
                      options={tradingTerms}
                      selectedValue={String(
                        data.markets[activeMarket]?.trading_terms || ""
                      )}
                      showRadioButton={true}
                      setSelectedValue={(val) => {
                        const valueAsString = String(val); // Cast number to string
                        setServiceScope((prev) => ({
                          ...prev,
                          tradingTerms: [valueAsString],
                        }));
                        updateMarketData(activeMarket, {
                          tradingTerms: [valueAsString],
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "trading_terms",
                          val,
                          setData
                        );
                      }}
                    />
                    {/* Minimum Exclusivity Tenure */}
                    <SimpleSingleSelectButtonGroup
                      error={validationError.minimumExclusivityTenure ?? ""}
                      label="Minimum Exclusivity Tenure"
                      options={minimumExclusivityTenure}
                      selectedValue={
                        String(data.markets[activeMarket]?.minimum_tenure) || ""
                      }
                      showRadioButton={true}
                      setSelectedValue={(val) => {
                        const valueAsString = String(val); // Cast number to string
                        setServiceScope((prev) => ({
                          ...prev,
                          minimumExclusivityTenure: [valueAsString],
                        }));
                        updateMarketData(activeMarket, {
                          minimumExclusivityTenure: [valueAsString],
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "minimum_tenure",
                          val,
                          setData
                        );
                      }}
                    />
                    {/* Preference on Marketing Investment */}
                    <SimpleSingleSelectButtonGroup
                      label="Preference on Marketing Investment"
                      error={validationError.marketingInv ?? ""}
                      options={marketing_investment}
                      selectedValue={String(
                        data.markets[activeMarket]
                          ?.trade_marketing_investments || ""
                      )}
                      showRadioButton={true}
                      setSelectedValue={(val) => {
                        const valueAsString = String(val); // Cast number to string
                        setServiceScope((prev) => ({
                          ...prev,
                          preferenceonMarketingInvestment: [valueAsString],
                        }));
                        updateMarketData(activeMarket, {
                          preferenceonMarketingInvestment: [valueAsString],
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "trade_marketing_investments",
                          val,
                          setData
                        );
                      }}
                    />
                    {/* Payment Terms */}
                    <SimpleSingleSelectButtonGroup
                      label="Payment Terms"
                      error={validationError.paymentTerms ?? ""}
                      options={paymentTerms}
                      selectedValue={String(
                        data.markets[activeMarket]?.payment_terms || ""
                      )}
                      showRadioButton={true}
                      setSelectedValue={(val) => {
                        const valueAsString = String(val); // Cast number to string
                        setServiceScope((prev) => ({
                          ...prev,
                          paymentTerms: [valueAsString],
                        }));
                        updateMarketData(activeMarket, {
                          paymentTerms: [valueAsString],
                        });
                        updateMarketServiceCapabilities(
                          activeMarket,
                          "payment_terms",
                          [val],
                          setData
                        );
                      }}
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddMarketModal
        isOpen={isAddMarketModalOpen}
        setIsOpen={setAddMarketIsModalOpen}
        selectedToAdd={selectedToAdd}
        setSelectedToAdd={setSelectedToAdd}
        selectedMarkets={selectedMarkets}
        setSelectedMarkets={setSelectedMarkets}
        allMarkets={allMarkets}
        setData={setData}
      />
      <DeleteConfirmationModal
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default ServiceAndScopePage;
